import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DaySummary } from "./DaySummary";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { ListItem } from "./ListItem";
import { searchSpots } from "../lib/search";
import { regionById } from "../lib/regions";
import type { ScoredDay, SpeedUnit, Spot } from "../lib/types";
import styles from "./SearchModal.module.css";

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * May be plain `Spot` (static, no forecast yet) or enriched spots once
   * the forecast finishes loading. When enriched, each result row shows
   * today's `DaySummary`; until then the row renders without one.
   */
  spots: readonly Spot[];
  unit: SpeedUnit;
}

/**
 * Search card overlay — Figma node 116:5950. Sits in a dimmed backdrop,
 * surface-raised card with rounded corners. Width follows the content
 * column up to --content-max-width; height hugs content. Autocompletes
 * across spot name / area / region / subregion / note as the user
 * types; each result row shows the spot's wind summary for today on
 * the right.
 *
 * Closes on:
 *  - Close button click
 *  - Escape key
 *  - Backdrop click
 *  - Picking a result (auto-close after navigate)
 */
export function SearchModal({ open, onClose, spots, unit }: SearchModalProps) {
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Close on Escape; autofocus the input when opened.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    inputRef.current?.focus();
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Reset state when the modal closes — nothing worse than reopening
  // with stale query + results.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const results = useMemo(() => searchSpots(query, spots), [query, spots]);

  if (!open) return null;

  const handlePick = (spotId: string) => {
    onClose();
    setLocation(`/spot/${spotId}`);
  };

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Search spots"
      onClick={(e) => {
        // Only close if the backdrop itself was clicked — not a child.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.card}>
        <header className={styles.header}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search spots, areas, regions"
            autoComplete="off"
            spellCheck={false}
          />
          <IconButton
            variant="elevated"
            onClick={onClose}
            aria-label="Close search"
          >
            <Icon name="close" />
          </IconButton>
        </header>

        {query.trim() === "" ? (
          <p className={[styles.hint, styles.hintCentered].join(" ")}>
            Search for spots, areas and regions
          </p>
        ) : results.length === 0 ? (
          <p className={styles.hint}>
            No spots match "{query.trim()}".
          </p>
        ) : (
          <div className={styles.results}>
            {results.map((r) => {
              // `days` only exists on EnrichedSpot — when the forecast
              // hasn't arrived yet we just render the row without the
              // summary so search stays usable on first paint.
              const today = (r.spot as Partial<{ days: ScoredDay[] }>).days?.[0];
              const region = regionById(r.spot.region);
              const subtitle = region
                ? `${r.spot.area} · ${region.label}`
                : r.spot.area;
              return (
                <ListItem
                  key={r.spot.id}
                  title={r.spot.name}
                  subtitle={subtitle}
                  surface="elevated"
                  compact
                  onClick={() => handlePick(r.spot.id)}
                >
                  {today && (
                    <DaySummary
                      day={today}
                      unit={unit}
                      idealDirs={r.spot.ideal.dirs}
                    />
                  )}
                </ListItem>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
